import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  MapPin,
  ArrowRight,
  ArrowLeft,
  Info,
  CheckCircle,
  Pause,
  Play,
} from "lucide-react";
import { format } from "date-fns";

import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { useToast } from "@shared/hooks/use-toast";
import ApplicationHeader from "./ApplicationHeader";
import MainLayout from "./MainLayout";
import OpenInNewTab from "./OpenInNewTab";
import { useWorkflow } from "./WorkflowContext";
import WorkflowProgress from "./WorkflowProgress";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useServiceOrder, useSkipSiteInspection, useUpdateStatus } from "../../hooks";
import { useUpdateConsumerService } from "../../hooks"; // Add this import
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import { Badge } from "@shared/ui/badge";
const SiteInspection = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Service Connection Workflow");

  const { consumerId: consumerId } = useParams<{ consumerId: string }>();
  const {
    application,
    workflowSteps,
    moveToNextStep,
    updateApplicationStatus,
    skipToStep,
  } = useWorkflow();
  const status = useParams<{ status: string }>();
  const statusDisplay = application?.apiStatus;
  const navigate = useNavigate();
  const location = useLocation();
  console.log("application", application);

  const { toast } = useToast();

  // Access acceptance data from navigation state
  const acceptanceData = location.state?.acceptanceData;
  //   const stateConsumerId = location.state?.consumerId;

  const [searchTerm, setSearchTerm] = useState("");

  // Initialize the update consumer service mutation
  const updateConsumerServiceMutation = useUpdateConsumerService();
  const skipSiteInspectionMutation = useSkipSiteInspection();
  const updateStatus = useUpdateStatus(Number(consumerId));

  // Fetch service orders using the hook
  const {
    data: serviceOrdersResponse,
    isLoading: isLoadingServiceOrders,
    error: serviceOrdersError,
  } = useServiceOrder({
    remote_utility_id: remoteUtilityId, // Get from application context
    search_data: searchTerm,
  });

  // Transform API response to the format expected by the form
  const serviceOrderOptions = useMemo(() => {
    if (!serviceOrdersResponse?.result) {
      return [];
    }

    return serviceOrdersResponse.result.map((order: any) => ({
      label: ` ${order.name}`,
      value: order.id.toString(),
      data: {
        id: order.id,
        service_no: order.serviceNo,
        name: order.name,
        sop_type: order.sopType,
        sop_subtype: order.sopSubtype,
        short_name: order.shortName,
        est_total_cost: order.estTotalCost,
        est_effort: order.estEffort,
      },
    }));
  }, [serviceOrdersResponse]);

  // Define form fields for site inspection with dynamic service orders
  const siteInspectionFields: FormField[] = useMemo(
    () => [
      {
        name: "selectedServiceOrder",
        label: "Select Service Order",
        type: "select",
        required: true,
        options: serviceOrderOptions,
        placeholder: isLoadingServiceOrders
          ? "Loading service orders..."
          : "Select service order",
        disabled: isLoadingServiceOrders,
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "inspectionDate",
        label: "Select Date of Inspection",
        type: "datepicker",
        required: true,
        placeholder: "Select date",
        fullWidth: true,
    
        allowPast:false,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "specialInstructions",
        label: "Special Instructions",
        type: "textarea",
        required: false,
        placeholder: "Add any special instructions for the inspection...",
        rows: 4,
        maxLength: 500,
        fullWidth: true,
        classes: {
          container: "w-full mb-6",
          label: "text-sm font-medium mb-2 block",
          textarea:
            "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-workflow-blue focus:border-workflow-blue resize-none",
          error: "text-red-600 text-sm mt-1",
        },
      },
    ],
    [serviceOrderOptions, isLoadingServiceOrders]
  );

  // Create form using FormService
  const inspectionForm = FormService.createForm({
    fields: siteInspectionFields,
    mode: "onChange",
    validateOnMount: false,
  });

  useEffect(() => {
    startTracking();
    // If site inspection is not required, show a toast and allow skipping
    if (application && !application.requiresInspection) {
      toast({
        title: "Site Inspection Not Required",
        description:
          "You can skip directly to the approval step for this application.",
      });
    }
  }, [application?.requiresInspection, toast]);

  // Handle service orders loading error
  useEffect(() => {
    if (serviceOrdersError) {
      toast({
        title: "Error Loading Service Orders",
        description: "Failed to load service orders. Please try again.",
        variant: "destructive",
      });
    }
  }, [serviceOrdersError, toast]);

  if (!application) {
    return <div>Loading...</div>;
  }

  const handleFormSubmit = async (formData: any) => {
    const { selectedServiceOrder, inspectionDate, specialInstructions } =
      formData;

    try {
      // Find service order data for display
      const selectedOrder = serviceOrderOptions.find(
        (so) => so.value === selectedServiceOrder
      );
      const serviceOrderName = selectedOrder?.label || "Unknown Service Order";

      // Format the date for API (assuming you need ISO format or specific format)
      const formattedDate = format(new Date(inspectionDate), "yyyy-MM-dd");
      // Prepare payload for API call
      const payload: any = {
        is_workorder: 1,
        remote_utility_id: remoteUtilityId,
        sop: parseInt(selectedServiceOrder), // Use selected service order ID
        request_account_no: application?.id || "",
        request_id: consumerId,
        request_username: application?.customerName || "",
        mobile_number: application?.customerPhone || "",
        area: application?.addressData?.service?.AREA || "",
        sub_area: application?.addressData?.service?.SUBAREA || "",
        premise: application?.addressData?.service?.PREMISE || "",
        request_availability_date: formattedDate,
        remark: specialInstructions || "",
        is_application_approved: 1,
      };

      // Make API call
      await updateConsumerServiceMutation.mutateAsync(
        {
          consumerId: consumerId,
          payload: payload,
        },
        {
          onSuccess: () => {
            logTimeUntilStep("Site Inspection");
          },
        }
      );

      // Update application status and move to next step
      updateApplicationStatus("approval");
      moveToNextStep();

      toast({
        title: "Inspection scheduled successfully",
        description: `Site inspection scheduled for ${format(
          new Date(inspectionDate),
          "PPP"
        )} for service order: ${serviceOrderName}.`,
      });

      navigate(`/cx/consumer-onboarding/workflow/approval/${consumerId}/SURVEY INPROGRESS`);
    } catch (error) {
      toast({
        title: "Error scheduling inspection",
        description: "Failed to schedule the inspection. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSkip = () => {
    skipSiteInspectionMutation.mutate({
      consumerId: consumerId,
    });
    toast({
      title: "Inspection skipped",
      description: "Moving directly to the approval step.",
    });

    navigate(`/cx/consumer-onboarding/workflow/approval/${consumerId}/APPROVAL INPROGRESS`);
  };

  const handleBack = () => {
    navigate(
      `/cx/consumer-onboarding/view/${consumerId}/${application?.status}`
    );
  };

  const renderCustomFooter = () => (
    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
      <Button variant="outline" onClick={handleBack} type="button">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={handleSkip}
          type="button"
          disabled={
            statusDisplay?.toLowerCase() !== "accepted" ||
            application.progressStatusDisplay === "Hold"
          }
        >
          Skip Inspection
        </Button>
        <Button
          type="submit"
          disabled={
            isLoadingServiceOrders ||
            updateConsumerServiceMutation.isPending ||
            statusDisplay?.toLowerCase() !== "accepted" ||
            application.progressStatusDisplay === "Hold"
          }
        >
          {updateConsumerServiceMutation.isPending
            ? "Scheduling..."
            : "Schedule Inspection"}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );

    const handleAcknowledge = () => {
      // update the status in the database
      const acknowledge_date = new Date().toISOString();
      updateStatus.mutate({
        remote_utility_id: remoteUtilityId,
        progress_status: "IN_PROGRESS",
        acknowledge_date,
      });
      toast({
        title: "Application acknowledged",
        description:
          "The application has been acknowledged and is now in progress.",
      });
    };
  
    const handlePutOnHold = () => {
        try {
          updateStatus.mutate({
            remote_utility_id: remoteUtilityId,
            progress_status: "HOLD",
          });
  
          toast({
            title: "Application on hold",
            description: "The application has been put on hold.",
          });
        } catch (error) {
          console.error("Error putting application on hold:", error);
          toast({
            title: "Error",
            description: "Failed to put application on hold. Please try again.",
            variant: "destructive",
          });
        }
    };
  
    const handleResume = () => {
        try {
          updateStatus.mutate({
            remote_utility_id: remoteUtilityId,
            progress_status: "IN_PROGRESS",
          });
  
          toast({
            title: "Application resumed",
            description:
              "The application has been resumed and is now in progress.",
          });
        } catch (error) {
          console.error("Error resuming application:", error);
          toast({
            title: "Error",
            description: "Failed to resume application. Please try again.",
            variant: "destructive",
          });
        }
    };
  
    // Define action buttons
    const actionButtons: ActionButton[] = [
      {
        id: "acknowledge",
        label: "Acknowledge",
        icon: CheckCircle,
        variant: "default",
        onClick: handleAcknowledge,
        condition: (status) =>
          application.progressStatusDisplay === "Acknowledge Pending",
      },
      {
        id: "hold",
        label: "Put on Hold",
        icon: Pause,
        variant: "outline",
        onClick: handlePutOnHold,
        condition: (status) =>
          application.progressStatusDisplay === "In Progress",
      },
      {
        id: "resume",
        label: "Resume",
        icon: Play,
        variant: "default",
        onClick: handleResume,
        condition: (status) => application.progressStatusDisplay === "Hold",
      },
    ];
  
    const relevantActions = actionButtons.filter(
      (action) =>
        !action.condition || action.condition(application.progressStatusDisplay)
    );
  
    function getStatusClasses(status) {
      switch (status) {
        case "Acknowledge Pending":
          return "bg-yellow-100 text-yellow-800 border-yellow-300";
        case "In Progress":
          return "bg-green-100 text-green-800 border-green-300";
        case "Hold":
          return "bg-red-100 text-red-800 border-red-300";
        default:
          return "bg-gray-100 text-gray-800 border-gray-300";
      }
    }

  return (
    <>
     <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Service Connection Workflow{" "}
          <Badge
            className={getStatusClasses(application.progressStatusDisplay)}
          >
            {application.progressStatusDisplay}
          </Badge>
        </h1>
        <div className="flex gap-2">
          {relevantActions.map((action) => (
            <Button
              key={action.id}
              size="sm"
              variant={action.variant}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4 mr-2" />
              {action.label}
            </Button>
          ))}
        </div>
      </div>
      <ApplicationHeader
        remote_utility_id={remoteUtilityId}
        is_detail_info={1}
      />

      <div className="mb-6">
        <WorkflowProgress
          steps={workflowSteps}
          currentStepId={application.currentStep}
        />
      </div>

      {!application.requiresInspection ? (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-workflow-blue" />
              Site Inspection Not Required
            </CardTitle>
            <CardDescription>
              This application type does not require a site inspection
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-6">
              <Info className="h-4 w-4" />
              <AlertTitle>Optional Step</AlertTitle>
              <AlertDescription>
                This application has been marked as not requiring a physical
                site inspection. You can proceed directly to the approval step.
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="justify-between">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button onClick={handleSkip}>
              Skip to Approval
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
  <div>
    <div className="flex items-center gap-2">
      <CalendarIcon className="h-5 w-5 text-workflow-blue" />
      <CardTitle>Site Inspection Scheduling</CardTitle>
    </div>
    <CardDescription className="ml-7">
      Schedule a site inspection to verify service location and technical requirements
    </CardDescription>
  </div>
</CardHeader>

              <CardContent className="mt-4">
                {/* Dynamic Form Implementation */}
                <DynamicForm
                  fields={siteInspectionFields}
                  form={inspectionForm}
                  onSubmit={handleFormSubmit}
                  renderCustomFooter={renderCustomFooter}
                  config={{
                    layout: "vertical",
                    spacing: "medium",
                    showResetButton: false,
                    classes: {
                      form: "space-y-6",
                      fieldContainer: "w-full",
                      buttonContainer: "hidden", // Hide default buttons since we're using custom footer
                    },
                  }}
                />
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle>Service Location</CardTitle>
                <OpenInNewTab
                  url="/cx/consumer-onboarding/workflow/inspection/preview"
                  className="h-8"
                >
                  View Map
                </OpenInNewTab>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md overflow-hidden h-[200px] bg-gray-100 flex items-center justify-center mb-4">
                  <div className="text-center">
                    <MapPin className="h-8 w-8 text-gray-400 mx-auto" />
                    <p className="text-sm text-gray-500 mt-2">
                      Map view would be displayed here
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h3 className="text-xs text-gray-500">Address</h3>
                    <p className="text-sm font-medium">
                      {application.serviceAddress.addressLine}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteInspection;
